import { LocationProvider, ErrorBoundary, Router, Route } from 'preact-iso';
import { render } from "preact";
import { useEffect } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import PropertiesSelect from './PropertiesSelect';
import DateSelect from "./DateSelect";
import { api } from './api';
import Dashboard from './pages/Dashboard';
import Sessions from './pages/Sessions';
import SessionDetail from './pages/SessionDetail';
import Settings from './pages/Settings';
import Login from './pages/Login';
import {
  authToken,
  buildDashboardUrl,
  buildLoginUrl,
  clearAuthTokenFromUrl,
  getDashboardEmbedMode,
  isAuthenticated,
  isEmbeddedDashboardMode,
  setAuthenticationState,
} from './state';

const ProtectedRoute = ({ component: Component, ...rest }: any) => {
  const location = useLocation();
  
  useEffect(() => {
    // Verifica autenticazione al caricamento
    if (isAuthenticated.value === false && location.path !== '/dashboard/login') {
      window.location.href = buildLoginUrl();
    }
  }, [location.path]);

  if (isAuthenticated.value !== true) {
    return null;
  }

  return <Component {...rest} />;
};

const DashboardLayout = ({ children }: { children: any }) => {
  const isEmbedded = isEmbeddedDashboardMode();
  const embedMode = getDashboardEmbedMode();
  const isDateOnlyEmbed = embedMode === '2';

  return (
    <div>
      <header className="dashboard-header">
        <div className="header-content">
          {!isDateOnlyEmbed && (
            <div className="header-left">
              <h1 className="dashboard-title">
                <span className="icon">🐟</span>
                Branzino Analytics
              </h1>
              <nav className="dashboard-nav">
                <a href={buildDashboardUrl('/dashboard')} className="nav-link">Dashboard</a>
                <a href={buildDashboardUrl('/dashboard/sessions')} className="nav-link">Sessioni</a>
                <a href={buildDashboardUrl('/dashboard/settings')} className="nav-link">Impostazioni</a>
                {!isEmbedded && (
                  <a href="/logout" target="_self" className="nav-link">Logout</a>
                )}
              </nav>
            </div>
          )}
          <div className="dashboard-controls">
            {!isEmbedded && !isDateOnlyEmbed && <PropertiesSelect />}
            <DateSelect />
          </div>
        </div>
      </header>
      <main>
        {children}
      </main>
    </div>
  );
};

const ProtectedDashboard = () => (
  <ProtectedRoute component={() => (
    <DashboardLayout>
      <Dashboard />
    </DashboardLayout>
  )} />
);

const ProtectedSessions = () => (
  <ProtectedRoute component={() => (
    <DashboardLayout>
      <Sessions />
    </DashboardLayout>
  )} />
);

const ProtectedSessionDetail = (props: any) => (
  <ProtectedRoute component={() => (
    <DashboardLayout>
      <SessionDetail {...props} />
    </DashboardLayout>
  )} />
);

const ProtectedSettings = () => (
  <ProtectedRoute component={() => (
    <DashboardLayout>
      <Settings />
    </DashboardLayout>
  )} />
);

const App = () => {
  useEffect(() => {
    const bootstrapAuth = async () => {
      try {
        if (authToken.value) {
          await api('/api/auth/token-login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ authToken: authToken.value }),
            redirectOnUnauthorized: false,
            displayErrorModal: false,
          });
          clearAuthTokenFromUrl();
          setAuthenticationState(true);
          return;
        }

        await api('/api/auth/session', {
          redirectOnUnauthorized: false,
          displayErrorModal: false,
        });
        setAuthenticationState(true);
      } catch {
        setAuthenticationState(false);
      }
    };

    void bootstrapAuth();
  }, []);

  if (isAuthenticated.value === null) {
    return null;
  }

  return (
    <LocationProvider scope="/dashboard">
      <ErrorBoundary>
        <Router>
          <Route path="/dashboard/login" component={Login} />
          <Route path="/dashboard" component={ProtectedDashboard} />
          <Route path="/dashboard/sessions" component={ProtectedSessions} />
          <Route path="/dashboard/sessions/:sessionId" component={ProtectedSessionDetail} />
          <Route path="/dashboard/settings" component={ProtectedSettings} />
          <Route default component={ProtectedDashboard} />
        </Router>
      </ErrorBoundary>
    </LocationProvider>
  );
};

render(<App />, document.getElementById("app")!);
